import { useState, useEffect, useMemo, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
  Filler,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colormaps, defaultColormap } from "./colormaps";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

const WORD_LANE = {
  heightDesktop: 40,
  heightMobile: 34,
  gapFromPlot: 8,

  fontDesktop: 16,
  fontMobile: 14,

  padXDesktop: 16,
  padXMobile: 14,

  minChipDesktop: 40,
  minChipMobile: 32,

  radius: 999,
};

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function makeLanePlugin(getLanePx) {
  return {
    id: "lanePlugin",

    beforeLayout(chart, _args, opts) {
      const lanePx = getLanePx(!!opts?.isMobile);
      const x = chart.scales?.x;
      if (!x) return;

      const prevAfterFit = x.options.afterFit;

      x.options.afterFit = (scale) => {
        if (typeof prevAfterFit === "function") prevAfterFit(scale);

        if (scale.$laneBaseHeight == null) {
          scale.$laneBaseHeight = scale.height || 0;
        }
        scale.height = scale.$laneBaseHeight + lanePx;
      };
    },

    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        wordIntervals = [],
        activeWord = null,
        hoveredWord = null,
        cursorX = null,
        showWordLabels = true,
        isMobile = false,
        xWindowMin = null,
        xWindowMax = null,
      } = pluginOptions || {};

      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      if (!ctx || !chartArea || !xScale) return;

      const { top, bottom, left, right } = chartArea;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const gap = WORD_LANE.gapFromPlot;
      const laneTop = bottom - laneH;
      const laneBottom = bottom;

      const fontSize = isMobile ? WORD_LANE.fontMobile : WORD_LANE.fontDesktop;
      const padX = isMobile ? WORD_LANE.padXMobile : WORD_LANE.padXDesktop;
      const minChipW = isMobile
        ? WORD_LANE.minChipMobile
        : WORD_LANE.minChipDesktop;

      const clipMin =
        Number.isFinite(Number(xWindowMin)) ? Number(xWindowMin) : null;
      const clipMax =
        Number.isFinite(Number(xWindowMax)) ? Number(xWindowMax) : null;

      ctx.save();

      ctx.beginPath();
      ctx.rect(left, top, right - left, bottom - top);
      ctx.clip();

      ctx.fillStyle = "rgba(15,23,42,0.035)";
      ctx.strokeStyle = "rgba(148,163,184,0.30)";
      ctx.lineWidth = 1;
      roundRect(ctx, left, laneTop, right - left, laneH, 12);
      ctx.fill();
      ctx.stroke();

      ctx.strokeStyle = "rgba(148,163,184,0.20)";
      ctx.beginPath();
      ctx.moveTo(left, laneTop - gap + 0.5);
      ctx.lineTo(right, laneTop - gap + 0.5);
      ctx.stroke();

      const chipY = laneTop + 3;
      const chipH = laneH - 6;

      ctx.font = `600 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";

      for (const w of wordIntervals) {
        const startC = clipMin == null ? w.start : Math.max(w.start, clipMin);
        const endC = clipMax == null ? w.end : Math.min(w.end, clipMax);
        if (!(endC > startC)) continue;

        const x1 = xScale.getPixelForValue(startC);
        const x2 = xScale.getPixelForValue(endC);

        const segLeft = Math.min(x1, x2);
        const segW = Math.max(1, Math.abs(x2 - x1));
        const segCenter = segLeft + segW / 2;

        const drawW = Math.max(minChipW, segW);
        let chipX = segLeft;
        if (segW < minChipW) chipX = segCenter - drawW / 2;

        const inset = 2;
        chipX = clamp(chipX, left + inset, right - inset - drawW);

        const isActive =
          activeWord &&
          w.start === activeWord.start &&
          w.end === activeWord.end;

        const isHovered =
          hoveredWord &&
          w.start === hoveredWord.start &&
          w.end === hoveredWord.end;

        const isActiveHovered = isActive && isHovered;

        if (isActiveHovered) {
          ctx.fillStyle = "rgba(16,185,129,0.30)";
          ctx.strokeStyle = "rgba(16,185,129,1)";
          ctx.lineWidth = 2.2;

          ctx.shadowColor = "rgba(16,185,129,0.35)";
          ctx.shadowBlur = 14;
          ctx.shadowOffsetY = 4;

          const lift = 2.5;

          ctx.save();
          ctx.shadowColor = "rgba(16,185,129,0.20)";
          ctx.shadowBlur = 18;
          roundRect(
            ctx,
            chipX - 1,
            chipY - lift - 1,
            drawW + 2,
            chipH + 2,
            WORD_LANE.radius
          );
          ctx.fillStyle = "rgba(16,185,129,0.08)";
          ctx.fill();
          ctx.restore();

          roundRect(ctx, chipX, chipY - lift, drawW, chipH, WORD_LANE.radius);
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          roundRect(ctx, chipX, chipY - lift, drawW, chipH, WORD_LANE.radius);
          ctx.stroke();
        } else if (isActive) {
          ctx.fillStyle = "rgba(16,185,129,0.22)";
          ctx.strokeStyle = "rgba(16,185,129,0.95)";
          ctx.lineWidth = 1.4;

          ctx.shadowColor = "rgba(0,0,0,0.10)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.stroke();
        } else if (isHovered) {
          ctx.fillStyle = "rgba(241,245,249,0.95)";
          ctx.strokeStyle = "rgba(59,130,246,0.75)";
          ctx.lineWidth = 1.2;

          ctx.shadowColor = "rgba(0,0,0,0.12)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;

          roundRect(
            ctx,
            chipX,
            chipY - 0.5,
            drawW,
            chipH + 1,
            WORD_LANE.radius
          );
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          roundRect(
            ctx,
            chipX,
            chipY - 0.5,
            drawW,
            chipH + 1,
            WORD_LANE.radius
          );
          ctx.stroke();
        } else {
          ctx.fillStyle = "rgba(241,245,249,0.70)";
          ctx.strokeStyle = "rgba(148,163,184,0.55)";
          ctx.lineWidth = 1;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.fill();
          ctx.stroke();
        }

        if (showWordLabels) {
          const available = drawW - padX * 2;
          let text = w.text ?? "";

          if (ctx.measureText(text).width > available) {
            while (
              text.length > 1 &&
              ctx.measureText(text + "…").width > available
            ) {
              text = text.slice(0, -1);
            }
            if (text !== (w.text ?? "")) text += "…";
          }

          ctx.fillStyle = isActiveHovered
            ? "rgba(4,120,87,1)"
            : isActive
            ? "rgba(5,150,105,1)"
            : isHovered
            ? "rgba(37,99,235,0.95)"
            : "rgba(51,65,85,0.90)";

          ctx.fillText(text, chipX + drawW / 2, chipY + chipH / 2);
        }
      }

      if (cursorX != null) {
        const xC = xScale.getPixelForValue(cursorX);
        if (xC >= left && xC <= right) {
          ctx.strokeStyle = "rgba(255,255,255,0.90)";
          ctx.lineWidth = isMobile ? 3 : 3.5;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();

          ctx.strokeStyle = "rgba(16,185,129,1)";
          ctx.lineWidth = isMobile ? 1.6 : 1.8;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();
        }
      }

      ctx.restore();
    },
  };
}

const ColorBar = ({ colormap, min = 41, max = 86 }) => {
  const steps = 100;
  const gradientStops = [];

  for (let i = 0; i <= steps; i++) {
    const normalized = i / steps;
    const mappedIntensity = normalized * 90;
    const color =
      colormap?.getColor?.(mappedIntensity) || "rgb(100, 100, 100)";
    gradientStops.push(`${color} ${normalized * 100}%`);
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span>{min}dB</span>
        <div
          className="flex-1 h-4 rounded"
          style={{
            background: `linear-gradient(to right, ${gradientStops.join(", ")})`,
          }}
        />
        <span>{max}dB</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>50dB</span>
        <span>60dB</span>
        <span>70dB</span>
        <span>80dB</span>
      </div>
    </div>
  );
};

export default function PitchIntensityAnimation({
  intensity,
  pitch,
  segment,
  broadcastTime,
  showPitch = true,
  selectedColormap = defaultColormap || "speechStable",
  onColormapChange,
  onWordClick,
}) {
  const [internalColormap, setInternalColormap] = useState(selectedColormap);
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);
  const [hoveredWord, setHoveredWord] = useState(null);

  const currentColormap = onColormapChange ? selectedColormap : internalColormap;

  const pitchXs = useMemo(() => pitch?.xs || [], [pitch]);
  const pitchYsRaw = useMemo(() => pitch?.ys || [], [pitch]);
  const intensityXs = useMemo(() => intensity?.xs || [], [intensity]);
  const intensityYs = useMemo(() => intensity?.ys || [], [intensity]);

  const wordIntervals = useMemo(() => {
    const segStart = Number(segment?.start ?? 0);
    return (
      segment?.words?.map((w) => ({
        start: Number(w.start) - segStart,
        end: Number(w.end) - segStart,
        text: String(w.text ?? ""),
      })) || []
    );
  }, [segment]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && wordIntervals.length <= 18);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [wordIntervals.length]);

  const handleColormapChange = useCallback(
    (newColormap) => {
      if (onColormapChange) {
        onColormapChange(newColormap);
      } else {
        setInternalColormap(newColormap);
      }
    },
    [onColormapChange]
  );

  const pitchYs = useMemo(() => {
    const out = new Array(pitchYsRaw.length);
    for (let i = 0; i < pitchYsRaw.length; i++) {
      const v = Number(pitchYsRaw[i]);
      out[i] = isFiniteNum(v) && v > 0 ? v : null;
    }
    return out;
  }, [pitchYsRaw]);

  const smoothedPitchYs = useMemo(() => {
    const data = pitchYs;
    const windowSize = isMobile ? 2 : 3;
    const smoothed = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i] == null) {
        smoothed.push(null);
        continue;
      }

      let sum = 0;
      let count = 0;

      for (
        let j = Math.max(0, i - windowSize);
        j <= Math.min(data.length - 1, i + windowSize);
        j++
      ) {
        if (data[j] != null) {
          sum += data[j];
          count++;
        }
      }

      smoothed.push(count ? sum / count : null);
    }

    return smoothed;
  }, [pitchYs, isMobile]);

  const voicedValues = useMemo(() => {
    return smoothedPitchYs.filter((v) => v != null && isFiniteNum(v));
  }, [smoothedPitchYs]);

  const yMin = useMemo(() => {
    let m = Infinity;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.min(m, voicedValues[i]);
    }
    return Number.isFinite(m) ? m : 0;
  }, [voicedValues]);

  const yMax = useMemo(() => {
    let m = 1;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.max(m, voicedValues[i]);
    }
    return m;
  }, [voicedValues]);

  const yRange = useMemo(() => Math.max(1, yMax - yMin), [yMax, yMin]);

  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!pitchXs.length || !smoothedPitchYs.length) {
      return { voicedXMin: 0, voicedXMax: 1 };
    }

    let first = -1;
    let last = -1;

    for (let i = 0; i < smoothedPitchYs.length; i++) {
      if (smoothedPitchYs[i] != null) {
        first = i;
        break;
      }
    }

    for (let i = smoothedPitchYs.length - 1; i >= 0; i--) {
      if (smoothedPitchYs[i] != null) {
        last = i;
        break;
      }
    }

    if (first === -1 || last === -1) {
      return {
        voicedXMin: pitchXs[0] ?? 0,
        voicedXMax: pitchXs[pitchXs.length - 1] ?? 1,
      };
    }

    return {
      voicedXMin: pitchXs[first],
      voicedXMax: pitchXs[last],
    };
  }, [pitchXs, smoothedPitchYs]);

  const cursorX = useMemo(() => {
    if (!pitchXs.length) return null;
    const t = Number(broadcastTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [broadcastTime, pitchXs.length, voicedXMin, voicedXMax]);

  const activeWord = useMemo(() => {
    if (cursorX == null) return null;
    return (
      wordIntervals.find((w) => cursorX >= w.start && cursorX <= w.end) || null
    );
  }, [cursorX, wordIntervals]);

  const shouldShowWordLabels = useMemo(() => {
    return (
      showWordLabels && wordIntervals.length > 0 && wordIntervals.length <= 18
    );
  }, [showWordLabels, wordIntervals.length]);

  const lanePxGetter = useMemo(() => {
    return (mobile) =>
      (mobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop) +
      WORD_LANE.gapFromPlot;
  }, []);

  const lanePlugin = useMemo(() => makeLanePlugin(lanePxGetter), [lanePxGetter]);

  const pickWordAtEvent = useCallback(
    (event, chart) => {
      if (!chart) return null;
      const { chartArea, scales } = chart;
      const xScale = scales?.x;
      if (!xScale || !chartArea) return null;

      const pos = getRelativePosition(event, chart);
      const xPx = pos.x;
      const yPx = pos.y;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const laneTop = chartArea.bottom - laneH;
      const laneBottom = chartArea.bottom;

      if (xPx < chartArea.left || xPx > chartArea.right) return null;
      if (yPx < laneTop || yPx > laneBottom) return null;

      const t = xScale.getValueForPixel(xPx);
      return wordIntervals.find((w) => t >= w.start && t <= w.end) || null;
    },
    [isMobile, wordIntervals]
  );

  const data = useMemo(() => {
    return {
      datasets: [
        {
          label: "Pitch (Hz)",
          data: pitchXs.map((x, i) => ({ x, y: smoothedPitchYs[i] })),
          borderColor: "#2563eb",
          borderWidth: isMobile ? 1.75 : 2.25,
          tension: isMobile ? 0.25 : 0.42,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          showLine: true,
          spanGaps: false,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          fill: !!showPitch,
          backgroundColor: (context) => {
            if (!showPitch) return "rgba(0,0,0,0)";

            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return null;

            const gradient = ctx.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0
            );

            const cmap =
              colormaps[currentColormap] ||
              colormaps[defaultColormap] ||
              colormaps.speechStable;

            if (!cmap?.getColor) {
              return "rgba(100, 100, 100, 0.4)";
            }

            const fixedMin = 41;
            const fixedMax = 86;
            const fixedRange = fixedMax - fixedMin || 1;
            const steps = 100;

            for (let i = 0; i <= steps; i++) {
              const xPercent = i / steps;
              const xValue =
                voicedXMin + xPercent * (voicedXMax - voicedXMin);

              let closestIntensity = fixedMin;
              let minDistance = Infinity;

              for (let j = 0; j < intensityXs.length; j++) {
                const xi = Number(intensityXs[j]);
                const yi = Number(intensityYs[j] ?? fixedMin);
                const distance = Math.abs(xi - xValue);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestIntensity = yi;
                }
              }

              const clampedIntensity = clamp(
                closestIntensity,
                fixedMin,
                fixedMax
              );
              const normalizedIntensity =
                (clampedIntensity - fixedMin) / fixedRange;
              const mappedIntensity = normalizedIntensity * 90;

              try {
                const color = cmap.getColor(mappedIntensity);
                const transparentColor = color
                  .replace("rgb(", "rgba(")
                  .replace(")", ", 0.4)");
                gradient.addColorStop(xPercent, transparentColor);
              } catch {
                gradient.addColorStop(xPercent, "rgba(100, 100, 100, 0.4)");
              }
            }

            return gradient;
          },
          datalabels: { display: false },
        },
      ],
    };
  }, [
    pitchXs,
    smoothedPitchYs,
    isMobile,
    showPitch,
    currentColormap,
    voicedXMin,
    voicedXMax,
    intensityXs,
    intensityYs,
  ]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },

      onHover: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        setHoveredWord(w);
        if (chart?.canvas) chart.canvas.style.cursor = w ? "pointer" : "default";
      },

      onClick: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        if (w && typeof onWordClick === "function") onWordClick(w);
      },

      scales: {
        x: {
          type: "linear",
          min: voicedXMin,
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: Math.max(0, yMin - yRange * 0.15),
          max: yMax * 1.05,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          title: {
            display: true,
            text: "Pitch (Hz)",
            color: "rgba(51,65,85,0.80)",
            font: { size: isMobile ? 12 : 13, weight: "600" },
          },
          ticks: {
            color: "rgba(51,65,85,0.70)",
            font: { size: isMobile ? 10 : 11 },
            maxTicksLimit: 5,
          },
        },
      },

      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
          callbacks: {
            afterBody: (context) => {
              const xValue = Number(context?.[0]?.parsed?.x);

              let closestIntensity = 0;
              let minDistance = Infinity;

              for (let i = 0; i < intensityXs.length; i++) {
                const xi = Number(intensityXs[i]);
                const yi = Number(intensityYs[i] ?? 0);
                const distance = Math.abs(xi - xValue);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestIntensity = yi;
                }
              }

              return [`Intensity: ${closestIntensity.toFixed(1)} dB`];
            },
          },
        },
        datalabels: { display: false },

        lanePlugin: {
          wordIntervals,
          activeWord,
          hoveredWord,
          cursorX,
          showWordLabels: shouldShowWordLabels,
          isMobile,
          xWindowMin: voicedXMin,
          xWindowMax: voicedXMax,
        },
      },

      interaction: { mode: undefined, axis: "x", intersect: false },
      elements: {
        point: { radius: 0, hoverRadius: 0, hitRadius: 0 },
      },
    };
  }, [
    isMobile,
    yMin,
    yMax,
    yRange,
    voicedXMin,
    voicedXMax,
    intensityXs,
    intensityYs,
    wordIntervals,
    activeWord,
    hoveredWord,
    cursorX,
    shouldShowWordLabels,
    onWordClick,
    pickWordAtEvent,
  ]);

  if (!pitchXs.length || !intensityXs.length) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <div className="flex flex-col gap-2">
        <label
          className={`flex items-center gap-2 select-none ${
            isMobile ? "text-xs" : "text-sm"
          }`}
        >
          Color Map:
          <select
            value={currentColormap}
            onChange={(e) => handleColormapChange(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {Object.entries(colormaps).map(([key, cm]) => (
              <option key={key} value={key}>
                {cm.name} - {cm.description}
              </option>
            ))}
          </select>
        </label>

        <ColorBar
          colormap={
            colormaps[currentColormap] ||
            colormaps[defaultColormap] ||
            colormaps.speechStable
          }
          min={41}
          max={86}
        />
      </div>

      {isMobile && wordIntervals.length > 0 && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels((v) => !v)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
        onMouseLeave={() => setHoveredWord(null)}
      >
        <Line data={data} options={options} plugins={[lanePlugin]} />
      </div>

      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom • Word labels: {showWordLabels ? "ON" : "OFF"}
        </p>
      )}
    </div>
  );
}